// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

/**
 * The current user's monitoring access, for the organization they are in.
 *
 * Reads the person's `PractitionerRole` at this organization rather than any
 * role they hold elsewhere: a nurse who runs monitoring at one clinic and
 * covers reception at another should get the module at the first and not the
 * second, and a project-wide role lookup would give them both.
 *
 * Returns `loading` separately from a negative answer, because the two must not
 * render the same. Treating "not loaded yet" as "no access" makes the module
 * flicker out of the nav on every page load, which reads as a bug and teaches
 * people to reload.
 */

import { useMedplum } from '@medplum/react';
import { useEffect, useState } from 'react';
import type { Practitioner, PractitionerRole } from '@medplum/fhirtypes';
import { useOptionalOrganization } from '../contexts/OrganizationContext';
import type { MonitoringAccess } from '../utils/monitoringAccess';
import { resolveMonitoringAccess } from '../utils/monitoringAccess';

export interface MonitoringAccessState extends MonitoringAccess {
  readonly loading: boolean;
}

export function useMonitoringAccess(): MonitoringAccessState {
  const medplum = useMedplum();
  const organization = useOptionalOrganization();
  const currentOrganization = organization?.currentOrganization;
  const profile = medplum.getProfile() as Practitioner | undefined;
  const isProjectAdmin = medplum.getProjectMembership()?.admin === true;

  const [role, setRole] = useState<{ key: string; value?: PractitionerRole }>();
  const key = `${profile?.id ?? ''}:${currentOrganization?.id ?? ''}`;
  const current = role?.key === key ? role : undefined;

  /*
   * A project admin is not gated, so their role is never read — which also
   * saves a query on every load for the account most likely to be demoing the
   * product. Same for a session with no profile or no organization: there is
   * nothing to look up.
   *
   * Expressed as a derived flag rather than an early `setState` inside the
   * effect. Setting state synchronously there triggers a cascading render on
   * every organization switch, and for an admin it would fire on every mount
   * to record an answer already known from the membership.
   */
  const needsLookup = !isProjectAdmin && Boolean(profile?.id) && Boolean(currentOrganization?.id);

  useEffect(() => {
    let active = true;
    if (!needsLookup || !profile?.id || !currentOrganization?.id) {
      return () => {
        active = false;
      };
    }

    medplum
      .searchResources('PractitionerRole', {
        practitioner: `Practitioner/${profile.id}`,
        organization: `Organization/${currentOrganization.id}`,
        _count: '5',
      })
      .then((roles) => {
        if (active) {
          /* Active assignments only — a lapsed role must not carry access. */
          setRole({ key, value: roles.find((entry) => entry.active !== false) });
        }
      })
      .catch(() => {
        if (active) {
          setRole({ key });
        }
      });

    return () => {
      active = false;
    };
  }, [medplum, profile?.id, currentOrganization?.id, needsLookup, key]);

  const resolved = resolveMonitoringAccess({
    role: current?.value,
    organization: currentOrganization ?? undefined,
    isProjectAdmin,
  });

  /*
   * Only a pending lookup counts as loading. An admin, or a session with
   * nothing to look up, has its answer immediately — reporting `loading` for
   * those would leave the nav item in its show-while-loading state for ever.
   */
  return { ...resolved, loading: needsLookup && current === undefined };
}
